// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	EXPRESSIONS_DESCRIPTOR as EXPRESSIONS_CATEGORY_DESCRIPTOR,
	INTEGRATIONS_DESCRIPTOR as INTEGRATIONS_CATEGORY_DESCRIPTOR,
} from '@app/features/user/components/settings_utils/section_registry/SharedDescriptors';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {
	ArrowCounterClockwiseIcon,
	BookOpenIcon,
	BrowserIcon,
	CompassIcon,
	GearIcon,
	HammerIcon,
	type Icon,
	type IconWeight,
	LightningIcon,
	LinkIcon,
	ProhibitIcon,
	ShieldCheckIcon,
	ShieldIcon,
	SignpostIcon,
	SmileyIcon,
	StickerIcon,
	TagIcon,
	TicketIcon,
	UserIcon,
	WaveformIcon,
	WebhooksLogoIcon,
} from '@phosphor-icons/react';

const SERVER_TAG_DESCRIPTOR = msg({
	message: 'Server Tag',
	context: 'community-settings-tab',
	comment: 'Community settings tab for server tag configuration.',
});
const PULSE_DESCRIPTOR = msg({
	message: 'ND Pulse',
	context: 'community-settings-tab',
	comment: 'Community settings tab for ND Pulse server boosting.',
});
const ONBOARDING_DESCRIPTOR = msg({
	message: 'Onboarding',
	context: 'community-settings-tab',
	comment: 'Community settings tab for server onboarding and welcome setup.',
});
const EMOJI_DESCRIPTOR = msg({
	message: 'Emoji',
	context: 'community-settings-tab',
	comment: 'Community settings tab for managing custom emoji uploaded to the community.',
});
const STICKERS_DESCRIPTOR = msg({
	message: 'Stickers',
	context: 'community-settings-tab',
	comment: 'Community settings tab for managing custom stickers uploaded to the community.',
});
const SOUNDBOARD_DESCRIPTOR = msg({
	message: 'Soundboard',
	context: 'community-settings-tab',
	comment: 'Community settings tab for managing soundboard sounds uploaded to the community.',
});
const OVERVIEW_DESCRIPTOR = msg({
	message: 'Overview',
	context: 'community-settings-tab',
	comment: 'Community settings tab for basic server/community details.',
});
const ROLES_DESCRIPTOR = msg({
	message: 'Roles',
	context: 'community-settings-tab',
	comment: 'Community settings tab for role hierarchy and permission rules.',
});
const MODERATION_DESCRIPTOR = msg({
	message: 'Moderation',
	context: 'community-settings-tab',
	comment: 'Community settings tab for moderation and safety controls.',
});
const AUTOMOD_DESCRIPTOR = msg({
	message: 'AutoMod',
	context: 'community-settings-tab',
	comment: 'Community settings tab for automated safety filters and word blacklists.',
});
const ACTIVITY_LOG_DESCRIPTOR = msg({
	message: 'Activity log',
	context: 'community-settings-tab',
	comment: 'Community settings tab showing activity log entries and moderation history.',
});
const ROLLBACK_DESCRIPTOR = msg({
	message: 'Rollback & Backups',
	context: 'community-settings-tab',
	comment: 'Community settings tab for server 1-click rollback and disaster recovery backups.',
});
const WEBHOOKS_DESCRIPTOR = msg({
	message: 'Webhooks',
	context: 'community-settings-tab',
	comment: 'Community settings tab for configuring webhooks.',
});
const WIDGET_DESCRIPTOR = msg({
	message: 'Widget',
	context: 'community-settings-tab',
	comment: 'Community settings tab for configuring server embed widgets.',
});
const VANITY_URL_DESCRIPTOR = msg({
	message: 'Vanity URL',
	context: 'community-settings-tab',
	comment: 'Community settings tab for the community vanity/custom invite link.',
});
const DISCOVERY_DESCRIPTOR = msg({
	message: 'Discovery',
	context: 'community-settings-tab',
	comment: 'Community settings tab for public community discovery/listing.',
});
const MEMBERS_DESCRIPTOR = msg({
	message: 'Members',
	context: 'community-settings-tab',
	comment: 'Community settings tab for managing community members.',
});
const INVITES_DESCRIPTOR = msg({
	message: 'Invites',
	context: 'community-settings-tab',
	comment: 'Community settings tab for invite link management.',
});
const BANS_DESCRIPTOR = msg({
	message: 'Bans',
	context: 'community-settings-tab',
	comment: 'Community settings tab for banned users.',
});

export type GuildSettingsTabType =
	| 'overview'
	| 'roles'
	| 'emoji'
	| 'stickers'
	| 'soundboard'
	| 'pulse'
	| 'tag'
	| 'moderation'
	| 'automod'
	| 'audit_log'
	| 'rollback'
	| 'webhooks'
	| 'widget'
	| 'onboarding'
	| 'vanity_url'
	| 'discovery'
	| 'members'
	| 'invites'
	| 'bans';
export type GuildSettingsTabCategories =
	| 'guild_settings'
	| 'expressions'
	| 'community'
	| 'integrations'
	| 'user_management';

export interface GuildSettingsTab {
	type: GuildSettingsTabType;
	category: GuildSettingsTabCategories;
	label: string;
	icon: Icon;
	iconWeight?: IconWeight;
	permission?: bigint | ReadonlyArray<bigint>;
	requireFeature?: string;
}

interface GuildSettingsTabDescriptor {
	type: GuildSettingsTabType;
	category: GuildSettingsTabCategories;
	label: MessageDescriptor;
	icon: Icon;
	iconWeight?: IconWeight;
	permission?: bigint | ReadonlyArray<bigint>;
	requireFeature?: string;
}

const GUILD_SETTINGS_TABS_DESCRIPTORS: Array<GuildSettingsTabDescriptor> = [
	{
		type: 'overview',
		category: 'guild_settings',
		label: OVERVIEW_DESCRIPTOR,
		icon: GearIcon,
		permission: Permissions.MANAGE_GUILD,
	},
	{
		type: 'roles',
		category: 'guild_settings',
		label: ROLES_DESCRIPTOR,
		icon: ShieldIcon,
		permission: Permissions.MANAGE_ROLES,
	},
	{
		type: 'tag',
		category: 'guild_settings',
		label: SERVER_TAG_DESCRIPTOR,
		icon: TagIcon,
		permission: Permissions.MANAGE_GUILD,
	},
	{
		type: 'moderation',
		category: 'guild_settings',
		label: MODERATION_DESCRIPTOR,
		icon: HammerIcon,
		permission: Permissions.MANAGE_GUILD,
	},
	{
		type: 'automod',
		category: 'guild_settings',
		label: AUTOMOD_DESCRIPTOR,
		icon: ShieldCheckIcon,
		permission: [Permissions.MANAGE_GUILD, Permissions.MODERATE_MEMBERS],
	},
	{
		type: 'audit_log',
		category: 'guild_settings',
		label: ACTIVITY_LOG_DESCRIPTOR,
		icon: BookOpenIcon,
		permission: Permissions.VIEW_AUDIT_LOG,
	},
	{
		type: 'rollback',
		category: 'guild_settings',
		label: ROLLBACK_DESCRIPTOR,
		icon: ArrowCounterClockwiseIcon,
		permission: Permissions.MANAGE_GUILD,
	},
	{
		type: 'emoji',
		category: 'expressions',
		label: EMOJI_DESCRIPTOR,
		icon: SmileyIcon,
		permission: [Permissions.CREATE_EXPRESSIONS, Permissions.MANAGE_EXPRESSIONS],
	},
	{
		type: 'stickers',
		category: 'expressions',
		label: STICKERS_DESCRIPTOR,
		icon: StickerIcon,
		permission: [Permissions.CREATE_EXPRESSIONS, Permissions.MANAGE_EXPRESSIONS],
	},
	{
		type: 'soundboard',
		category: 'expressions',
		label: SOUNDBOARD_DESCRIPTOR,
		icon: WaveformIcon,
		permission: [Permissions.CREATE_EXPRESSIONS, Permissions.MANAGE_EXPRESSIONS, Permissions.MANAGE_GUILD],
	},
	{
		type: 'pulse',
		category: 'community',
		label: PULSE_DESCRIPTOR,
		icon: LightningIcon,
		iconWeight: 'fill',
	},

	{
		type: 'onboarding',
		category: 'community',
		label: ONBOARDING_DESCRIPTOR,
		icon: SignpostIcon,
		iconWeight: 'fill',
		permission: Permissions.MANAGE_GUILD,
	},
	{
		type: 'discovery',
		category: 'community',
		label: DISCOVERY_DESCRIPTOR,
		icon: CompassIcon,
		iconWeight: 'fill',
		permission: Permissions.MANAGE_GUILD,
	},
	{
		type: 'vanity_url',
		category: 'community',
		label: VANITY_URL_DESCRIPTOR,
		icon: LinkIcon,
		iconWeight: 'bold',
		permission: Permissions.MANAGE_GUILD,
		requireFeature: GuildFeatures.VANITY_URL,
	},
	{
		type: 'webhooks',
		category: 'integrations',
		label: WEBHOOKS_DESCRIPTOR,
		icon: WebhooksLogoIcon,
		permission: Permissions.MANAGE_WEBHOOKS,
	},
	{
		type: 'widget',
		category: 'integrations',
		label: WIDGET_DESCRIPTOR,
		icon: BrowserIcon,
		permission: Permissions.MANAGE_GUILD,
	},
	{
		type: 'members',
		category: 'user_management',
		label: MEMBERS_DESCRIPTOR,
		icon: UserIcon,
		permission: Permissions.MANAGE_GUILD,
	},
	{
		type: 'invites',
		category: 'user_management',
		label: INVITES_DESCRIPTOR,
		icon: TicketIcon,
		permission: Permissions.MANAGE_GUILD,
	},
	{
		type: 'bans',
		category: 'user_management',
		label: BANS_DESCRIPTOR,
		icon: ProhibitIcon,
		permission: Permissions.BAN_MEMBERS,
	},
];
export const GUILD_SETTINGS_LABEL_DESCRIPTOR = msg({
	message: 'Community settings',
	comment: 'Root label for community settings modal and settings search paths.',
});
const COMMUNITY_CATEGORY_DESCRIPTOR = msg({
	message: 'Community',
	comment: 'Community settings sidebar category grouping discovery and the vanity URL.',
});
const PEOPLE_CATEGORY_DESCRIPTOR = msg({
	message: 'People',
	comment: 'Community settings sidebar category grouping members, invites, and bans.',
});

export function getGuildSettingsCategoryLabel(i18n: I18n, category: GuildSettingsTabCategories): string {
	switch (category) {
		case 'guild_settings':
			return '';
		case 'expressions':
			return i18n._(EXPRESSIONS_CATEGORY_DESCRIPTOR);
		case 'community':
			return i18n._(COMMUNITY_CATEGORY_DESCRIPTOR);
		case 'integrations':
			return i18n._(INTEGRATIONS_CATEGORY_DESCRIPTOR);
		case 'user_management':
			return i18n._(PEOPLE_CATEGORY_DESCRIPTOR);
	}
}

function getGuildSettingsTabLabel(i18n: I18n, tabType: GuildSettingsTabType): string {
	const tab = GUILD_SETTINGS_TABS_DESCRIPTORS.find((candidate) => candidate.type === tabType);
	return tab ? i18n._(tab.label) : '';
}

export function formatGuildSettingsPath(i18n: I18n, tabType: GuildSettingsTabType): string {
	return [i18n._(GUILD_SETTINGS_LABEL_DESCRIPTOR), getGuildSettingsTabLabel(i18n, tabType)].filter(Boolean).join(' > ');
}

export const getGuildSettingsTabs = (i18n: I18n): Array<GuildSettingsTab> => {
	return GUILD_SETTINGS_TABS_DESCRIPTORS.map((tab) => ({
		...tab,
		label: getGuildSettingsTabLabel(i18n, tab.type),
	}));
};
